/**
 * Event landing-page template — 10-corporate ("Private Circle · Annual Members Suite").
 *
 * Distinct CraftJS home-page document for the `corporate` theme. Cloned from the
 * proven `webinar` template and re-skinned to the "marble-brass-suite" style:
 * marble white/light-grey surfaces, brass/antique-gold accents, elegant serif
 * headers, refined corporate tone, subtle rounding (rounded-md). Authored from
 *   TEMPLATES - LANDINPAGES/10-corporate/sections/*.html (CRAFT_PROPS_HINT blocks).
 *
 * Convention (mirrors webinar/temp/welcome):
 *  - Section-level marketing copy (headlines, paragraphs, button labels) goes
 *    through td() keys (`corp*` prefix) → localized.
 *  - Repetitive structured child data (agenda rows, speakers, FAQ, …) also go
 *    through td() (`corp*` keys, Wave 3b) — only markup, times, numerals and
 *    fictitious proper names stay inline.
 *  - Global colors/fonts come from corporatePreset (applied via the theme);
 *    only sections with their own surface (Hero/Testimonials over dark) set
 *    explicit per-node colors here.
 */
import { defaultRegistrationConsents, toCheckboxItems } from "@/lib/website/registrationConsents";
import {
  A,
  enc,
  pv,
  tc,
  btn,
  fi,
  acceptanceFi,
  scaffold,
  emptyFieldsGroup,
  PAD,
  DIST_CARD,
  BTN_FORM_SP,
  SEC_MARGIN,
  ACCEPTANCE_VISIBILITY_FILTER,
  type N,
  type TranslationFn,
} from "../temp";
import { EASE, decorLayer, fadeUpKeyframes, fontFaces, heroShell, motionGate, revealBlock } from "./premium-css";
import { direction } from "@/lib/designDirections/directions";
import type { WebsiteThemeStylePreset } from "@/types/WebsiteTheme";
import {
  SAMPLE_SPEAKER_IMAGES,
  SAMPLE_SPONSOR_LOGOS,
} from "@/lib/website-builder/sampleContent";

// Corporate-specific asset namespace (seeded under <CDN>/corporate/…)
const W = `${A}/shared/templates/10-corporate`;

// WS4-B2: corporate palette is registry-synced — the values are byte-equal to
// the shared design-directions canon, so the consts read straight from it.
const ACCENT = direction("corporate").primary; // brass (#7A602E) — darkened from #8b6f3a for WCAG on marble (links/CTA label 5.23:1, was 4.18:1)
const GOLD = direction("corporate").accent; // antique gold (#C9A96E) — bright, use ONLY on dark surfaces (dark-band eyebrow 6.88:1); on light marble it fails (1.97:1)
const GOLD_TEXT = "#6f5628"; // dark antique-gold for eyebrows on the LIGHT marble surface — 6.09:1 (bright GOLD there is only 1.97:1)
const INK = direction("corporate").text; // near-black warm headings (#2A2419)
const MUTE = "#6b6353"; // muted warm body
const MARBLE = direction("corporate").background; // marble surface (#F5F0E8)
const SURFACE = "#faf6ef"; // lighter surface
const DARK = direction("corporate").text; // dark section surface (#2A2419, footer/testimonials)
const ON_DARK = "#d8cfbf"; // body text on dark
const ON_DARK_ACCENT = direction("corporate").accent; // eyebrow on dark (#C9A96E)
const SERIF = "'Bodoni 72', 'Bodoni Moda', Bodoni, Didot, 'Times New Roman', serif";
// WS4-B2: body follows the direction canon ("Adobe Caslon Pro") — the css
// registers lora-* woff2 under that family name, Lora/Georgia as fallbacks.
const BODY = "'Adobe Caslon Pro', Lora, Georgia, serif";

// Speaker/testimonial faces come from the shared sample rotation
// (lib/website-builder/sampleContent); the template's own `<family>/*`
// art stays what it is: heroes, details and gallery photography.
const LOGO = `${A}/shared/logo.svg`;

// ── Child data (localized via td — Wave 3b; markup/numerals stay inline) ──

const NUMBERS_DATA = (td: TranslationFn) => [
  { num: "<b>XII</b>", desc: td("corpNum1Desc") },
  { num: "<b>144</b>", desc: td("corpNum2Desc") },
  { num: "<b>37</b>", desc: td("corpNum3Desc") },
  { num: `<b>${td("corpNum4Value")}</b>`, desc: td("corpNum4Desc") },
];

const AGENDA_DATA = (td: TranslationFn) => [
  { time: "<b>I · 16:00</b>", title: td("corpAgendaRow1Title"), desc: td("corpAgendaRow1Desc") },
  { time: "<b>II · 16:30</b>", title: td("corpAgendaRow2Title"), desc: td("corpAgendaRow2Desc") },
  { time: "<b>III · 17:15</b>", title: td("corpAgendaRow3Title"), desc: td("corpAgendaRow3Desc") },
  { time: "<b>IV · 18:00</b>", title: td("corpAgendaRow4Title"), desc: td("corpAgendaRow4Desc") },
  { time: "<b>V · 19:00</b>", title: td("corpAgendaRow5Title"), desc: td("corpAgendaRow5Desc") },
  { time: "<b>VI · 19:30</b>", title: td("corpAgendaRow6Title"), desc: td("corpAgendaRow6Desc") },
];

const SPEAKER_DATA = (td: TranslationFn) => [
  { name: "Eleanor Brandt-Voss", role: td("corpSpeaker1Role"), img: "03" },
  { name: "Maximilian Reuter", role: td("corpSpeaker2Role"), img: "06" },
  { name: "Sophie de Laval", role: td("corpSpeaker3Role"), img: "05" },
  { name: "Friedrich Adelsheim", role: td("corpSpeaker4Role"), img: "12" },
  { name: "Cornelia Wittgenstein", role: td("corpSpeaker5Role"), img: "01" },
  { name: "Augustin Marteau", role: td("corpSpeaker6Role"), img: "04" },
];

const TESTIMONIAL_DATA = (td: TranslationFn) => [
  { quote: td("corpTesti1Quote"), name: td("corpTesti1Name"), role: td("corpTesti1Role"), img: "05" },
  { quote: td("corpTesti2Quote"), name: td("corpTesti2Name"), role: td("corpTesti2Role"), img: "06" },
  { quote: td("corpTesti3Quote"), name: td("corpTesti3Name"), role: td("corpTesti3Role"), img: "03" },
];

const BLOG_DATA = (td: TranslationFn) => [
  { topic: `<b>${td("corpBlog1Topic")}</b>`, content: td("corpBlog1Content") },
  { topic: `<b>${td("corpBlog2Topic")}</b>`, content: td("corpBlog2Content") },
  { topic: `<b>${td("corpBlog3Topic")}</b>`, content: td("corpBlog3Content") },
];

const FAQ_DATA = (td: TranslationFn) => [
  { q: `<b>${td("corpFaq1Question")}</b>`, a: td("corpFaq1Answer") },
  { q: `<b>${td("corpFaq2Question")}</b>`, a: td("corpFaq2Answer") },
  { q: `<b>${td("corpFaq3Question")}</b>`, a: td("corpFaq3Answer") },
  { q: `<b>${td("corpFaq4Question")}</b>`, a: td("corpFaq4Answer") },
  { q: `<b>${td("corpFaq5Question")}</b>`, a: td("corpFaq5Answer") },
  { q: `<b>${td("corpFaq6Question")}</b>`, a: td("corpFaq6Answer") },
];

// ── Corporate-specific child factories ────────────────────────────────

function numbersChild(parent: string, i: number, td: TranslationFn): N {
  const d = NUMBERS_DATA(td)[i];
  return {
    type: { resolvedName: "NumbersChild" }, isCanvas: false,
    props: {
      isVisible: true,
      isVisibleNumber: true, textNumber: d.num, fontSizeNumber: "text-4xl", textAlignNumber: "center",
      isVisibleDescription: true, textDescription: d.desc, fontSizeDescription: "text-sm", textAlignDescription: "center",
    },
    displayName: "NumbersChild", custom: { icon: "IconNumber" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function agendaChild(parent: string, i: number, td: TranslationFn): N {
  const d = AGENDA_DATA(td)[i];
  return {
    type: { resolvedName: "AgendaChild" }, isCanvas: false,
    props: {
      isVisibleText1: true, textText1: d.time, fontSizeText1: "text-sm", textAlignText1: "left",
      isVisibleText2: true, textText2: d.title, fontSizeText2: "text-sm", textAlignText2: "left",
      isVisibleText3: false, textText3: "", fontSizeText3: "text-sm", textAlignText3: "left",
      isVisibleText: true, textText: d.desc, fontSizeText: "text-sm", textAlignText: "left", border: true,
    },
    displayName: "AgendaChild", custom: { icon: "IconCalendar" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function speakerChild(parent: string, i: number, td: TranslationFn): N {
  const d = SPEAKER_DATA(td)[i];
  return {
    type: { resolvedName: "SpeakerChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length], imageBottomBackground: "transparent",
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left",
      icons: [{ icon: "IconBrandLinkedin" }, { icon: "IconMail" }], isVisibleIcon: true,
    },
    displayName: "SpeakerChild", custom: { icon: "IconMicrophone" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function testimonialChild(parent: string, i: number, td: TranslationFn): N {
  const d = TESTIMONIAL_DATA(td)[i];
  return {
    type: { resolvedName: "TestimonialsChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPONSOR_LOGOS[i % SAMPLE_SPONSOR_LOGOS.length].src,
      imageUrlBottom: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length],
      isVisibleImageTop: false, imageBottomBackground: "transparent", isVisibleImageBottom: false,
      isVisibleContent: true, textContent: d.quote, fontSizeContent: "text-md", textAlignContent: "left", fontColorContent: ON_DARK,
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left", fontColorName: "#ffffff",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left", fontColorOccupation: ON_DARK,
    },
    displayName: "TestimonialsChild", custom: { icon: "IconThumbUp" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function blogChild(parent: string, i: number, td: TranslationFn): N {
  const d = BLOG_DATA(td)[i];
  return {
    type: { resolvedName: "BlogChild" }, isCanvas: false,
    props: {
      iconAlignment: "left", isVisible: true,
      isVisibleTopic: true, textTopic: d.topic, fontSizeTopic: "text-lg", textAlignTopic: "left",
      isVisibleContent: true, textContent: d.content, fontSizeContent: "text-md", textAlignContent: "left",
      isVisibleButton: false, size: "small", text: td("learnMore"), underline: false, border: true,
    },
    displayName: "BlogChild", custom: { icon: "IconArticle" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function faqChild(parent: string, i: number, td: TranslationFn): N {
  const d = FAQ_DATA(td)[i];
  return {
    type: { resolvedName: "FaQChild" }, isCanvas: false,
    props: {
      isVisibleQuestion: true, textQuestion: d.q, fontSizeQuestion: "text-sm", textAlignQuestion: "left",
      isVisibleAnswer: true, textAnswer: d.a, fontSizeAnswer: "text-sm", textAlignAnswer: "left", border: true,
    },
    displayName: "FaQChild", custom: { icon: "IconMessageQuestion" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

const GALLERY_IMG = [
  `${W}/hero-einladung.jpg`, `${W}/detail-marble-wax-seal.jpg`, `${W}/hero-dokumente.jpg`,
  `${W}/hero-treatment.jpg`, `${W}/detail-brass-key.jpg`, `${W}/hero-absage.jpg`,
];

function galleryImage(parent: string, i: number): N {
  return {
    type: { resolvedName: "ImageChild" }, isCanvas: false,
    props: { src: GALLERY_IMG[i % GALLERY_IMG.length], isVisible: true, aspectRatio: "aspect-video", displayName: "ImageChild", width: "w-full", canDuplicate: true },
    displayName: "ImageChild", custom: { icon: "IconSection" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

// ── Template ──────────────────────────────────────────────────────────

export const corporate = (td: TranslationFn): string => {
  const B = "corp_body";

  // Top-level section ids (body order)
  const HERO = "corp_hero";
  const NUMBERS = "corp_numbers";
  const AGENDA = "corp_agenda";
  const SPEAKER = "corp_speaker";
  const TEXT = "corp_text";
  const IMAGE = "corp_image";
  const COUNTDOWN = "corp_countdown";
  const GALLERY = "corp_gallery";
  const VIDEO = "corp_video";
  const TESTI = "corp_testimonials";
  const BLOG = "corp_blog";
  const FAQ = "corp_faq";
  const MAP = "corp_map";
  const DI = "corp_datainput";
  const CONTACT = "corp_contact";

  return enc({
    ...scaffold(
      { ab: "corp_ab", abs: "corp_abs", body: B, ft: "corp_ft", fts: "corp_fts" },
      [HERO, NUMBERS, DI, AGENDA, SPEAKER, TEXT, IMAGE, COUNTDOWN, GALLERY, VIDEO, TESTI, BLOG, FAQ, MAP, CONTACT],
      false, { background: "bg-white", padding: "p-4", borderRadius: "rounded-md" },
    ),

    // ── Hero (image right, dark serif text on marble) ──
    [HERO]: {
      type: { resolvedName: "Hero" }, isCanvas: false,
      props: {
        sectionHeight: 90, contentAlignment: "center", imageLayout: "right", layoutWidth: "50%", inversed: true,
        padding: PAD, distButtons: "gap-3 wb-md:gap-4", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", buttonAlignment: "flex-row", isVisible: true,
        isVisibleHeadline: true, textHeadline: td("corpHeroEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left", headlineColor: GOLD_TEXT,
        isVisibleHeader: true, textHeader: td("corpHeroHeader"), fontSizeHeader: "text-6xl", textAlignHeader: "left", headerColor: INK,
        isVisibleText: true, textText: td("corpHeroText"), fontSizeText: "text-lg", textAlignText: "left", textColor: MUTE,
        secondaryButtonSize: "medium", secondaryButtonText: td("corpHeroSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("corpHeroPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
        backgroundType: "color", imageRounded: false, backgroundOverlayColor: "transparent",
        imageSrc: `${W}/hero-einladung.jpg`, backgroundColor: MARBLE,
      },
      displayName: "Hero", custom: { icon: "IconSlideshow", preview: pv("Hero-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "corp_hero_eb", header: "corp_hero_hd", text: "corp_hero_tx", buttonOne: "corp_hero_b1", buttonTwo: "corp_hero_b2" },
    },
    corp_hero_eb: tc(HERO, { isVisible: true, text: td("corpHeroEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: GOLD_TEXT, header: true }),
    corp_hero_hd: tc(HERO, { isVisible: true, text: td("corpHeroHeader"), fontSize: "text-6xl", textAlign: "left", displayName: "header", color: INK, header: true }),
    corp_hero_tx: tc(HERO, { isVisible: true, text: td("corpHeroText"), fontSize: "text-lg", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    corp_hero_b1: btn(HERO, { type: "secondary", text: td("corpHeroSecondaryButton"), displayName: "buttonOne" }),
    corp_hero_b2: btn(HERO, { type: "primary", linkType: "internal", linkUrl: "#" + DI, text: td("corpHeroPrimaryButton"), displayName: "buttonTwo" }),

    // ── Numbers ──
    [NUMBERS]: {
      type: { resolvedName: "Numbers" }, isCanvas: true,
      props: {
        sectionHeight: 60, contentAlignment: "center", sectionMargin: SEC_MARGIN, gridColumns: "4",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("corpNumbersHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("corpNumbersHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("corpNumbersText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Numbers", custom: { icon: "Icon123", preview: pv("NumbersPreview"), allowedComponents: ["NumbersChild"] },
      parent: B, hidden: false, nodes: ["corp_num1", "corp_num2", "corp_num3", "corp_num4"],
      linkedNodes: { headline: "corp_num_eb", header: "corp_num_hd", text: "corp_num_tx" },
    },
    corp_num_eb: tc(NUMBERS, { isVisible: false, text: td("corpNumbersHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    corp_num_hd: tc(NUMBERS, { isVisible: true, text: td("corpNumbersHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    corp_num_tx: tc(NUMBERS, { isVisible: true, text: td("corpNumbersText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    corp_num1: numbersChild(NUMBERS, 0, td),
    corp_num2: numbersChild(NUMBERS, 1, td),
    corp_num3: numbersChild(NUMBERS, 2, td),
    corp_num4: numbersChild(NUMBERS, 3, td),

    // ── Agenda ──
    [AGENDA]: {
      type: { resolvedName: "Agenda" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("corpAgendaHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("corpAgendaHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("corpAgendaText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Agenda", custom: { icon: "IconListCheck", preview: pv("AgendaPreview"), allowedComponents: ["AgendaChild"] },
      parent: B, hidden: false, nodes: ["corp_ag1", "corp_ag2", "corp_ag3", "corp_ag4", "corp_ag5", "corp_ag6"],
      linkedNodes: { headline: "corp_ag_eb", header: "corp_ag_hd", text: "corp_ag_tx" },
    },
    corp_ag_eb: tc(AGENDA, { isVisible: false, text: td("corpAgendaHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    corp_ag_hd: tc(AGENDA, { isVisible: true, text: td("corpAgendaHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    corp_ag_tx: tc(AGENDA, { isVisible: true, text: td("corpAgendaText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    corp_ag1: agendaChild(AGENDA, 0, td),
    corp_ag2: agendaChild(AGENDA, 1, td),
    corp_ag3: agendaChild(AGENDA, 2, td),
    corp_ag4: agendaChild(AGENDA, 3, td),
    corp_ag5: agendaChild(AGENDA, 4, td),
    corp_ag6: agendaChild(AGENDA, 5, td),

    // ── Speaker (hidden by default — no portrait library) ──
    [SPEAKER]: {
      type: { resolvedName: "Speaker" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextSpeakers: "gap-8 wb-md:gap-12", distSpeakers: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("corpSpeakerHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("corpSpeakerHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("corpSpeakerText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Speaker", custom: { icon: "IconUserStar", preview: pv("Speaker-preview"), allowedComponents: ["SpeakerChild"] },
      parent: B, hidden: false, nodes: ["corp_sp1", "corp_sp2", "corp_sp3", "corp_sp4", "corp_sp5", "corp_sp6"],
      linkedNodes: { headline: "corp_sp_eb", header: "corp_sp_hd", text: "corp_sp_tx" },
    },
    corp_sp_eb: tc(SPEAKER, { isVisible: false, text: td("corpSpeakerHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    corp_sp_hd: tc(SPEAKER, { isVisible: true, text: td("corpSpeakerHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    corp_sp_tx: tc(SPEAKER, { isVisible: true, text: td("corpSpeakerText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    corp_sp1: speakerChild(SPEAKER, 0, td),
    corp_sp2: speakerChild(SPEAKER, 1, td),
    corp_sp3: speakerChild(SPEAKER, 2, td),
    corp_sp4: speakerChild(SPEAKER, 3, td),
    corp_sp5: speakerChild(SPEAKER, 4, td),
    corp_sp6: speakerChild(SPEAKER, 5, td),

    // ── Text (editorial note from the founder) ──
    [TEXT]: {
      type: { resolvedName: "TextSection" }, isCanvas: false,
      props: {
        buttonAlign: "center", padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("corpTextHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("corpTextHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("corpTextBody"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("corpTextSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false, secondaryButtonAlign: "center",
        primaryButtonSize: "medium", primaryButtonText: td("corpTextPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false, primaryButtonAlign: "center",
      },
      displayName: "Text", custom: { icon: "IconAlignJustified", preview: pv("textPreview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "corp_tx_eb", header: "corp_tx_hd", text: "corp_tx_tx", buttonOne: "corp_tx_b1", buttonTwo: "corp_tx_b2" },
    },
    corp_tx_eb: tc(TEXT, { isVisible: false, text: td("corpTextHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    corp_tx_hd: tc(TEXT, { isVisible: true, text: td("corpTextHeader"), fontSize: "text-4xl", textAlign: "center", displayName: "header", header: true }),
    corp_tx_tx: tc(TEXT, { isVisible: true, text: td("corpTextBody"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    corp_tx_b1: btn(TEXT, { type: "secondary", text: td("corpTextSecondaryButton"), displayName: "buttonOne" }),
    corp_tx_b2: btn(TEXT, { type: "primary", text: td("corpTextPrimaryButton"), displayName: "buttonTwo" }),

    // ── Image (the folio detail, image left / text right) ──
    [IMAGE]: {
      type: { resolvedName: "Image" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, inversed: true,
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true,
        isVisibleHeadline: false, textHeadline: td("corpImageHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("corpImageHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("corpImageText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("corpImageButton"), isVisibleButton: true, buttonAlign: "left",
        buttonBorder: true, buttonUnderline: false,
        imageRounded: "rounded-md", layoutWidth: "50%",
        isVisibleImage: true, imageSrc: `${W}/detail-folio.jpg`, imageAspectRatio: "aspect-[4/5]",
      },
      displayName: "Image", custom: { icon: "IconSection", preview: pv("Image-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "corp_im_eb", header: "corp_im_hd", text: "corp_im_tx", button: "corp_im_btn", image: "corp_im_img" },
    },
    corp_im_eb: tc(IMAGE, { isVisible: false, text: td("corpImageHeader"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    corp_im_hd: tc(IMAGE, { isVisible: true, text: td("corpImageHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    corp_im_tx: tc(IMAGE, { isVisible: true, text: td("corpImageText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    corp_im_btn: btn(IMAGE, { text: td("corpImageButton"), displayName: "button" }),
    corp_im_img: {
      type: { resolvedName: "ImageChild" }, isCanvas: false,
      props: { src: `${W}/detail-folio.jpg`, isVisible: true, aspectRatio: "aspect-[4/5]", displayName: "ImageChild", width: "w-full wb-lg:w-1/2", canDuplicate: false },
      displayName: "ImageChild", custom: { icon: "IconSection" },
      parent: IMAGE, hidden: false, nodes: [], linkedNodes: {},
    },

    // ── Countdown ──
    [COUNTDOWN]: {
      type: { resolvedName: "Countdown" }, isCanvas: false,
      props: {
        sectionHeight: 40, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distButtonCountdown: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true, buttonAlign: "left",
        isVisibleHeadline: true, textHeadline: td("corpCountdownEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("corpCountdownHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("corpCountdownText"), fontSizeText: "text-base", textAlignText: "left",
        buttonSize: "medium", buttonText: td("corpCountdownButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
        isVisiblCountdown: true, countdownSizeDate: "text-5xl", countdownFontTypeDate: SERIF, countdownFontColorDate: INK,
        countdownSize: "text-base", countdownFontColor: "#6b6353", countdownBorderColor: "#d8cfbf",
      },
      displayName: "Countdown", custom: { icon: "IconAlarm", preview: pv("Countdown-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "corp_cd_eb", header: "corp_cd_hd", text: "corp_cd_tx", button: "corp_cd_btn" },
    },
    corp_cd_eb: tc(COUNTDOWN, { isVisible: true, text: td("corpCountdownEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: GOLD_TEXT, header: true }),
    corp_cd_hd: tc(COUNTDOWN, { isVisible: true, text: td("corpCountdownHeader"), fontSize: "text-4xl", textAlign: "left", displayName: "header", color: INK, header: true }),
    corp_cd_tx: tc(COUNTDOWN, { isVisible: true, text: td("corpCountdownText"), fontSize: "text-base", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    corp_cd_btn: btn(COUNTDOWN, { text: td("corpCountdownButton"), displayName: "ButtonChild" }),

    // ── Gallery (estate, suite, tafel) — hidden by default ──
    [GALLERY]: {
      type: { resolvedName: "Gallery" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", distButtonGallery: "gap-3 wb-md:gap-4", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, buttonAlign: "center", gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("corpGalleryHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("corpGalleryHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("corpGalleryHeader"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("corpGallerySecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("corpGalleryPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
      },
      displayName: "Gallery", custom: { icon: "IconPhoto", preview: pv("Gallery-preview"), allowedComponents: ["ImageChild"] },
      parent: B, hidden: false,
      nodes: ["corp_g1", "corp_g2", "corp_g3", "corp_g4", "corp_g5", "corp_g6"],
      linkedNodes: { headline: "corp_g_eb", header: "corp_g_hd", text: "corp_g_tx", secondary: "corp_g_b2", primary: "corp_g_b1" },
    },
    corp_g_eb: tc(GALLERY, { isVisible: false, text: td("corpGalleryHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    corp_g_hd: tc(GALLERY, { isVisible: true, text: td("corpGalleryHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    corp_g_tx: tc(GALLERY, { isVisible: false, text: td("corpGalleryHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    corp_g_b2: btn(GALLERY, { type: "secondary", text: td("corpGallerySecondaryButton"), displayName: "ButtonChild" }),
    corp_g_b1: btn(GALLERY, { text: td("corpGalleryPrimaryButton"), displayName: "ButtonChild" }),
    corp_g1: galleryImage(GALLERY, 0),
    corp_g2: galleryImage(GALLERY, 1),
    corp_g3: galleryImage(GALLERY, 2),
    corp_g4: galleryImage(GALLERY, 3),
    corp_g5: galleryImage(GALLERY, 4),
    corp_g6: galleryImage(GALLERY, 5),

    // ── Video (recap) — present but hidden by default ──
    [VIDEO]: {
      type: { resolvedName: "Video" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentVideo: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false,
        isVisibleHeadline: false, textHeadline: td("corpVideoHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("corpVideoHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("corpVideoText"), fontSizeText: "text-md", textAlignText: "center",
        buttonSize: "medium", buttonText: td("corpVideoButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "center",
        videoAspectRatio: "aspect-video", isVisibleVideo: true, videoUrl: "https://youtu.be/sdESKm-nKQE",
      },
      displayName: "Video", custom: { icon: "IconVideo", preview: pv("Video-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "corp_vd_eb", header: "corp_vd_hd", text: "corp_vd_tx", button: "corp_vd_btn" },
    },
    corp_vd_eb: tc(VIDEO, { isVisible: false, text: td("corpVideoHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    corp_vd_hd: tc(VIDEO, { isVisible: true, text: td("corpVideoHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    corp_vd_tx: tc(VIDEO, { isVisible: true, text: td("corpVideoText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    corp_vd_btn: btn(VIDEO, { text: td("corpVideoButton"), isVisible: true, displayName: "button" }),

    // ── Testimonials (dark surface) — hidden by default (no portraits) ──
    [TESTI]: {
      type: { resolvedName: "Testimonials" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextTestimonials: "gap-8 wb-md:gap-12", distTestimonials: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: true, gridColumns: "3", layoutWidth: "100%",
        backgroundColor: DARK,
        isVisibleHeadline: true, textHeadline: td("corpTestimonialsEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("corpTestimonialsHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("corpTestimonialsHeader"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Testimonials", custom: { icon: "IconQuote", preview: pv("Testimonials-preview"), allowedComponents: ["TestimonialsChild"] },
      parent: B, hidden: false, nodes: ["corp_ts1", "corp_ts2", "corp_ts3"],
      linkedNodes: { headline: "corp_ts_eb", header: "corp_ts_hd", text: "corp_ts_tx" },
    },
    corp_ts_eb: tc(TESTI, { isVisible: true, text: td("corpTestimonialsEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ON_DARK_ACCENT, header: true }),
    corp_ts_hd: tc(TESTI, { isVisible: true, text: td("corpTestimonialsHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: "#ffffff", header: true }),
    corp_ts_tx: tc(TESTI, { isVisible: false, text: td("corpTestimonialsHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", color: ON_DARK, header: false }),
    corp_ts1: testimonialChild(TESTI, 0, td),
    corp_ts2: testimonialChild(TESTI, 1, td),
    corp_ts3: testimonialChild(TESTI, 2, td),

    // ── Blog (letters from the salon) — present but hidden by default ──
    [BLOG]: {
      type: { resolvedName: "Blog" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("corpBlogHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("corpBlogHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("corpBlogText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Blog", custom: { icon: "IconClick", preview: pv("Blog-preview"), allowedComponents: ["BlogChild"] },
      parent: B, hidden: false, nodes: ["corp_bl1", "corp_bl2", "corp_bl3"],
      linkedNodes: { headline: "corp_bl_eb", header: "corp_bl_hd", text: "corp_bl_tx" },
    },
    corp_bl_eb: tc(BLOG, { isVisible: false, text: td("corpBlogHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    corp_bl_hd: tc(BLOG, { isVisible: true, text: td("corpBlogHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    corp_bl_tx: tc(BLOG, { isVisible: true, text: td("corpBlogText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    corp_bl1: blogChild(BLOG, 0, td),
    corp_bl2: blogChild(BLOG, 1, td),
    corp_bl3: blogChild(BLOG, 2, td),

    // ── FaQ ──
    [FAQ]: {
      type: { resolvedName: "FaQ" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("corpFaqHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("corpFaqHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("corpFaqText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "FaQ", custom: { icon: "IconQuestionMark", preview: pv("faqPreview"), allowedComponents: ["FaQChild"] },
      parent: B, hidden: false, nodes: ["corp_fq1", "corp_fq2", "corp_fq3", "corp_fq4", "corp_fq5", "corp_fq6"],
      linkedNodes: { headline: "corp_fq_eb", header: "corp_fq_hd", text: "corp_fq_tx" },
    },
    corp_fq_eb: tc(FAQ, { isVisible: false, text: td("corpFaqHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    corp_fq_hd: tc(FAQ, { isVisible: true, text: td("corpFaqHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    corp_fq_tx: tc(FAQ, { isVisible: true, text: td("corpFaqText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    corp_fq1: faqChild(FAQ, 0, td),
    corp_fq2: faqChild(FAQ, 1, td),
    corp_fq3: faqChild(FAQ, 2, td),
    corp_fq4: faqChild(FAQ, 3, td),
    corp_fq5: faqChild(FAQ, 4, td),
    corp_fq6: faqChild(FAQ, 5, td),

    // ── Map (the estate) — present but hidden by default ──
    [MAP]: {
      type: { resolvedName: "Map" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentMap: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false,
        isVisibleHeadline: false, textHeadline: td("corpMapHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("corpMapHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("corpMapText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("corpMapButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "left",
        mapRounded: "rounded-md",
        mapZoom: 12, mapType: "m", aspectRatio: "aspect-[4/3]", isVisibleMap: true, mapLocation: td("corpMapLocation"),
      },
      displayName: "Map", custom: { icon: "IconMap2", preview: pv("Map-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "corp_mp_eb", header: "corp_mp_hd", text: "corp_mp_tx", button: "corp_mp_btn" },
    },
    corp_mp_eb: tc(MAP, { isVisible: false, text: td("corpMapHeader"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    corp_mp_hd: tc(MAP, { isVisible: true, text: td("corpMapHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    corp_mp_tx: tc(MAP, { isVisible: true, text: td("corpMapText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    corp_mp_btn: btn(MAP, { text: td("corpMapButton"), displayName: "button" }),

    // ── DataInput (multi-step reservation) ──
    [DI]: {
      type: { resolvedName: "DataInput" }, isCanvas: false,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, isVisible: true, textSpacing: DIST_CARD,
        backgroundColor: "transparent", border: false, borderWidth: "border-2", borderStyle: "border-solid",
        borderSides: ["bottom"], borderRadius: undefined,
        isVisibleHeadline: true, textHeadline: td("corpDataHeadline"), fontSizeHeadline: "text-base", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("corpDataHeader"), fontSizeHeader: "text-lg", textAlignHeader: "center",
        isVisibleText: true, textText: td("corpDataText"), fontSizeText: "text-sm", textAlignText: "center",
        allowUpdate: true,
        multiStepEnabled: true, stepLabels: [td("corpStepStatus"), td("corpStepPlusOne"), td("corpStepTour"), td("corpStepOverview")], multiStepMode: "pages",
        progressBarEnabled: true, progressBarColor: ACCENT,
        overviewEnabled: true, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []],
      },
      displayName: "DataInput", custom: { icon: "IconForms", preview: pv("DataInput-Preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "corp_di_hl", header: "corp_di_hd", text: "corp_di_tx",
        fieldsGroup0: "corp_di_f0", fieldsGroup1: "corp_di_f1", fieldsGroup2: "corp_di_f2",
        fieldsGroup3: "corp_di_f3", fieldsGroup4: "corp_di_f4",
        companionsGroup: "corp_di_comp", overviewGroup: "corp_di_ov", checkboxes: "corp_di_cb",
        button: "corp_di_btn", backButton: "corp_di_back", nextButton: "corp_di_next",
      },
    },
    corp_di_hl: tc(DI, { isVisible: true, text: td("corpDataHeadline"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "center", displayName: "headline", header: true }),
    corp_di_hd: tc(DI, { isVisible: true, text: td("corpDataHeader"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-lg", textAlign: "center", displayName: "header", header: true }),
    corp_di_tx: tc(DI, { isVisible: true, text: td("corpDataText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "center", displayName: "text", header: false }),
    corp_di_f0: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 4, groupIndex: 0, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["corp_di_acceptance", "corp_di_first", "corp_di_last", "corp_di_email"], linkedNodes: {},
    },
    // FIRST in the group, and filter-free — the question every other
    // field in this group is gated on. See `acceptanceFi`.
    corp_di_acceptance: acceptanceFi("corp_di_f0", td("acceptanceState")),
    corp_di_first: fi("corp_di_f0", { label: td("firstName"), requiredField: true, inputFieldVariable: "firstName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    corp_di_last: fi("corp_di_f0", { label: td("lastName"), requiredField: true, inputFieldVariable: "lastName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    corp_di_email: fi("corp_di_f0", { label: td("email"), requiredField: true, inputFieldVariable: "email", gridArea: "col-span-12", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    corp_di_f1: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 2, groupIndex: 1, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["corp_di_company", "corp_di_role"], linkedNodes: {},
    },
    corp_di_company: fi("corp_di_f1", { label: td("corpFieldCircle"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "company", changeable: true }),
    corp_di_role: fi("corp_di_f1", { label: td("corpFieldPlusOne"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "role", changeable: true }),
    corp_di_f2: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 1, groupIndex: 2, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["corp_di_questions"], linkedNodes: {},
    },
    corp_di_questions: fi("corp_di_f2", { label: td("corpFieldCuisine"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "questions", multiline: true, gridArea: "col-span-12", changeable: true }),
    corp_di_f3: emptyFieldsGroup(DI, 3),
    corp_di_f4: emptyFieldsGroup(DI, 4),
    corp_di_comp: {
      type: { resolvedName: "CompanionsGroup" }, isCanvas: true,
      props: { isVisible: false, fieldCount: 1, fieldSpacing: "medium", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER },
      displayName: "CompanionsGroup", custom: { icon: "IconUsers", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["corp_di_comp_first"], linkedNodes: {},
    },
    corp_di_comp_first: fi("corp_di_comp", { label: td("firstName"), requiredField: false, inputFieldVariable: "firstName", gridArea: "col-span-12", changeable: true, type: "companion" }),
    corp_di_ov_t: tc("corp_di_ov", { isVisible: true, text: td("overviewTitle"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "left", displayName: "overviewTitle", header: true }),
    corp_di_ov_x: tc("corp_di_ov", { isVisible: true, text: td("overviewText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "left", displayName: "overviewText", header: false }),
    corp_di_ov: {
      type: { resolvedName: "OverviewGroup" }, isCanvas: true,
      props: { isVisible: false, parentNodeId: DI, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []], stepLabels: [] },
      displayName: "summaryGroup", custom: { icon: "IconListDetails" },
      parent: DI, hidden: false, nodes: ["corp_di_ov_t", "corp_di_ov_x"], linkedNodes: {},
    },
    corp_di_cb: {
      type: { resolvedName: "Checkboxes" }, isCanvas: false,
      props: { isVisible: true, checkboxes: toCheckboxItems(defaultRegistrationConsents(td)), displayName: "checkboxes" },
      displayName: "Checkboxes", custom: { icon: "IconCheckbox" },
      parent: DI, hidden: false, nodes: [], linkedNodes: {},
    },
    corp_di_btn: btn(DI, { linkType: "internal", linkUrl: "", text: td("corpDataSubmit"), submittedText: "", isSubmitted: false, enableSubmittedState: true, size: "small", widthMode: "full", buttonType: "submit", margin: BTN_FORM_SP, displayName: "button" }),
    corp_di_back: btn(DI, { linkType: "internal", type: "secondary", linkUrl: "", text: td("back"), navigationRole: "back", size: "small", widthMode: "content", isVisible: false, displayName: "backButton" }),
    corp_di_next: btn(DI, { linkType: "internal", linkUrl: "", text: td("next"), navigationRole: "next", size: "small", widthMode: "content", isVisible: false, displayName: "nextButton" }),

    // ── Contact ──
    [CONTACT]: {
      type: { resolvedName: "Contact" }, isCanvas: true,
      props: {
        sectionHeight: 70, contentAlignment: "center", infoSection: true, backgroundColor: "transparent",
        padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-3 wb-md:space-y-5 wb-lg:space-y-6", isVisible: true,
        infoWidth: "50%", inversed: false,
        isVisibleHeadline: true, textHeadline: td("corpContactEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("corpContactHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("corpContactText"), fontSizeText: "text-md", textAlignText: "left",
        isSocialVisible: true, socialIcons: [{ platform: "linkedin", url: "https://linkedin.com" }, { platform: "instagram", url: "https://instagram.com" }], socialSize: 24, socialPosition: "left",
        buttonSize: "medium", buttonText: td("corpContactButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
      },
      displayName: "Contact", custom: { icon: "IconForms", preview: pv("Contact-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "corp_ct_eb", header: "corp_ct_hd", text: "corp_ct_tx",
        firstName: "corp_ct_first", lastName: "corp_ct_last", eMail: "corp_ct_email",
        phoneNumber: "corp_ct_phone", Message: "corp_ct_msg", button: "corp_ct_btn",
      },
    },
    corp_ct_eb: tc(CONTACT, { isVisible: true, text: td("corpContactEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: GOLD, header: true }),
    corp_ct_hd: tc(CONTACT, { isVisible: true, text: td("corpContactHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    corp_ct_tx: tc(CONTACT, { isVisible: true, text: td("corpContactText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    corp_ct_first: fi(CONTACT, { label: td("firstName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "firstName", changeable: false }),
    corp_ct_last: fi(CONTACT, { label: td("lastName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "lastName", changeable: false }),
    corp_ct_email: fi(CONTACT, { label: td("email"), overwriteType: "EMAIL", requiredField: true, inputFieldVariable: "email", changeable: false, locked: true }),
    corp_ct_phone: fi(CONTACT, { label: td("phoneNumber"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "phone", changeable: false }),
    corp_ct_msg: fi(CONTACT, { label: td("messageLabel"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "message", multiline: true, gridArea: "col-span-12", changeable: false, locked: true }),
    corp_ct_btn: btn(CONTACT, { linkType: "internal", linkUrl: "", text: td("corpContactButton"), widthMode: "full", buttonType: "submit", displayName: "button", fixedNavigation: false }),
  });
};

// ── Meta sections (AppBar + Footer) ───────────────────────────────────

/** Marble bar, brass top rule, logo + nav + "Reservation" CTA. */
export const corporateAppbar = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "AppBarSection" }, isCanvas: false,
    props: {
      padding: "p-4 wb-md:p-5 wb-lg:p-6", isVisible: true,
      // WS4-B2: deliberately NOT transparent — the hero is light marble, so a
      // see-through bar would sit light-on-light with no readable edge.
      backgroundColor: MARBLE,
      border: true, borderColor: ACCENT, borderWidth: "border", borderStyle: "border-solid", borderSides: ["top"],
      sectionOrder: ["logo", "links", "social"],
      isLogoVisible: true, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-10", logoPosition: "left",
      isLinksVisible: true, linksPosition: "center",
      links: [
        { label: td("corpLinkProgram"), url: "#corp_agenda", linkType: "internal" },
        { label: td("corpLinkMembers"), url: "#corp_speaker", linkType: "internal" },
        { label: td("corpLinkEstate"), url: "#corp_faq", linkType: "internal" },
        { label: td("corpLinkContact"), url: "#corp_contact", linkType: "internal" },
      ],
      linksColor: INK, linksFontSize: "text-sm", linksFontWeight: "font-medium",
      isSocialVisible: false, socialIcons: [], socialPosition: "right", socialSize: 24,
      logoutButtonSize: "small", logoutButtonText: td("corpHeroPrimaryButton"),
      logoutButtonBorder: true, logoutButtonBackgroundColor: ACCENT, logoutButtonColor: MARBLE, logoutButtonBorderColor: ACCENT,
    },
    displayName: "AppBar", custom: { icon: "IconLayoutNavbar" },
    parent: "DCH9WIIT55", hidden: false, nodes: [], linkedNodes: {},
  });

/** Dark footer, antique-gold top rule, four link groups + social + copyright. */
export const corporateFooter = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "FooterSection" }, isCanvas: false,
    props: {
      padding: "px-4 py-10 wb-md:px-6 wb-lg:px-8", isVisible: true,
      backgroundColor: DARK,
      // border-4 + borderSides:["top"] → getBorderClasses emits "border-t-4"
      // (passing "border-t-4" as the width would mis-emit "border-t-t-4").
      border: true, borderColor: GOLD, borderWidth: "border-4", borderStyle: "border-solid", borderSides: ["top"],
      // No footer logo (per design) — omit "logo" from sectionOrder + hide it.
      sectionOrder: ["links", "social"],
      isLogoVisible: false, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-auto",
      isLinkGroupsVisible: true,
      linkGroups: [
        { title: td("corpFooterProgram"), links: [
          { label: td("corpLinkProgram"), url: "#corp_agenda", linkType: "internal" },
          { label: td("corpLinkMembers"), url: "#corp_speaker", linkType: "internal" },
          { label: td("corpLinkLibrary"), url: "/login", linkType: "internal" },
        ] },
        // Event practicalities — Maps + add-to-calendar are EXTERNAL (open in a
        // new tab). URLs are placeholders the organiser edits per event.
        { title: td("corpFooterEvent"), links: [
          { label: td("corpLinkLocation"), url: "https://www.google.com/maps/search/?api=1&query=Estate", linkType: "external" },
          { label: td("corpLinkCalendar"), url: "https://calendar.google.com/calendar/render?action=TEMPLATE&text=Annual+Members+Suite", linkType: "external" },
          { label: td("corpLinkReservation"), url: "/register", linkType: "internal" },
        ] },
        { title: td("corpFooterSupport"), links: [
          { label: td("corpLinkContact"), url: "#corp_contact", linkType: "internal" },
          { label: td("corpLinkEstate"), url: "#corp_faq", linkType: "internal" },
          { label: td("corpLinkSecretariat"), url: "#corp_contact", linkType: "internal" },
        ] },
        { title: td("corpFooterLegal"), links: [
          { label: td("corpLinkPrivacy"), url: "/privacy", linkType: "internal" },
          { label: td("corpLinkImprint"), url: "/imprint", linkType: "internal" },
          { label: td("corpLinkTerms"), url: "/terms", linkType: "internal" },
        ] },
      ],
      linksTitleColor: "#ffffff", linksTitleFontSize: "text-sm", linksTitleFontWeight: "font-semibold",
      linksColor: "#bcae97", linksFontSize: "text-sm", linksFontWeight: "font-normal", linksHoverColor: "hover:font-primary",
      isSocialVisible: true,
      socialIcons: [
        { altText: "linkedin", platform: "linkedin", url: "https://linkedin.com" },
        { altText: "instagram", platform: "instagram", url: "https://instagram.com" },
      ],
      socialColor: "#bcae97", socialHoverColor: "hover:font-primary", socialSize: 20, socialTitle: td("followUs"),
      isCopyrightVisible: true, copyrightColor: "#8c8069", copyrightFontSize: "text-xs", copyrightCompanyName: td("yourCompany"),
    },
    displayName: "Footer", custom: { icon: "IconMenu2" },
    parent: "xaUW2UDZT8", hidden: false, nodes: [], linkedNodes: {},
  });

// ── Theme CSS (P9 WS4-B2) ─────────────────────────────────────────────
//
// Marker comment first so a later user edit is distinguishable from the
// pristine preset. Composed from the shared premium-css builders (WS3).
// The hero keeps its light split layout (photo right, node props untouched);
// the css layers the private-suite treatment underneath: a marble wash
// settling onto the base surface, an engraved gold hairline frame floating
// inside the hero, uppercase letterspaced kickers, and the self-hosted faces
// so "Bodoni 72"/"Adobe Caslon Pro" resolve identically everywhere
// (bodoni-moda + lora under the canon family names).
const CUSTOM_CSS = `/* theme:corporate */
${fontFaces([
  { family: "Bodoni 72", file: "bodoni-moda", weightRange: "400 900" },
  { family: "Adobe Caslon Pro", file: "lora-regular", weightRange: "400" },
  { family: "Adobe Caslon Pro", file: "lora-bold", weightRange: "700" },
  { family: "Adobe Caslon Pro", file: "lora-italic", weightRange: "400", style: "italic" },
])}

/* Suite hero — lighter marble sheet settling onto the base surface */
#corp_hero {
${heroShell}
  background-image: linear-gradient(180deg, ${SURFACE} 0%, ${MARBLE} 100%);
}

/* Engraved-stationery motif: a gold hairline frame floating inside the hero */
#corp_hero::before {
${decorLayer}
  inset: 18px;
  border: 1px solid rgba(201, 169, 110, 0.55);
}

/* Formal brass rule beneath the fold */
#corp_hero::after {
${decorLayer}
  left: 50%;
  bottom: 7%;
  width: 96px;
  height: 1px;
  margin-left: -48px;
  background: ${ACCENT};
}

/* Uppercase letterspaced kickers (the text-xs eyebrows in each section) */
#corp_hero .text-xs, #corp_numbers .text-xs, #corp_agenda .text-xs, #corp_speaker .text-xs,
#corp_text .text-xs, #corp_image .text-xs, #corp_countdown .text-xs, #corp_gallery .text-xs,
#corp_video .text-xs, #corp_testimonials .text-xs, #corp_blog .text-xs, #corp_faq .text-xs,
#corp_map .text-xs, #corp_contact .text-xs {
  text-transform: uppercase;
  letter-spacing: 0.22em;
}

${motionGate(`${fadeUpKeyframes("corp", 16)}
  @keyframes corpFrameIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  /* Quiet suite entrance: the sheet settles, the gold frame fades in */
  #corp_hero { animation: corpFadeUp 0.8s ${EASE} both; }
  #corp_hero::before {
    animation: corpFrameIn 1.6s ${EASE} 0.4s both;
  }

${revealBlock("corp", ["numbers", "datainput", "agenda", "speaker", "text", "image", "countdown", "gallery", "video", "testimonials", "blog", "faq", "map", "contact"], 30)}`)}
`;

// ── Style preset (marble-brass-suite) ─────────────────────────────────

export const corporatePreset: WebsiteThemeStylePreset = {
  heroImage: `${W}/hero-einladung.jpg`,
  logoSrc: LOGO,
  colorPalette: { primary: ACCENT, secondary: GOLD, background: MARBLE },
  fonts: { header: SERIF, body: BODY },
  rounding: "rounded-md",
  recommendedSize: "large",
  bodyTheme: { fontColor: MUTE, fontSize: "16px", layoutOption: "full-width" },
  headingsTheme: {
    // WS4-B2 display bump: text-6xl hero → 48px h1, 28px h2 (didone luxe).
    h1: { fontFamily: SERIF, fontSize: "48px", fontColor: INK },
    h2: { fontFamily: SERIF, fontSize: "28px", fontColor: INK },
  },
  buttonsTheme: {
    // Primary stays SOLID brass (marble label 5.23:1, hover 6.66:1 on white);
    // secondary is a true ghost (transparent, brass hairline) — WS4-B2 pairing.
    primary: {
      bgColor: ACCENT, bgHoverColor: "#71591f", borderColor: ACCENT, borderHoverColor: "#71591f",
      fontColor: MARBLE, fontHoverColor: "#ffffff", fontSize: "16px", fontHoverSize: "16px",
      borderRadius: "rounded-md", hoverBorderRadius: "rounded-md",
    },
    secondary: {
      bgColor: "transparent", bgHoverColor: ACCENT, borderColor: ACCENT, borderHoverColor: ACCENT,
      fontColor: ACCENT, fontHoverColor: MARBLE, fontSize: "16px", fontHoverSize: "16px",
      borderRadius: "rounded-md", hoverBorderRadius: "rounded-md",
    },
  },
  hyperlinkTheme: { fontColor: ACCENT, fontHoverColor: "#71591f", underline: false },
  formInputTheme: {
    labelFontColor: MUTE, inputFontColor: INK, backgroundColor: SURFACE,
    border: true, borderColor: "#d8cfbf", borderRadius: "rounded-md", borderStyle: "border-solid",
    borderWidth: "border", borderSides: [], fontFamily: BODY,
    fontSize: "text-sm", errorColor: "#DC2626",
  },
  customCss: CUSTOM_CSS,
};
